import {
  ApeironBlueButton,
  DEFAULT_ANIMATE_DURATION,
  NextImage,
  NextLink,
  noForwardProps
} from '@apeiron/temp'
import { ApeironLoadingNow } from '@apeiron/ui'
import { Box, IconButton, styled, Typography } from '@mui/material'
import { keyframes } from '@mui/system'
import ButtonIconPNG from '@public/icons/icon-battle-demo-lootbox-button.png'
import VipPassLootBoxPopup from '@src/components/battle/demo/vip-pass/VipPassLootBoxPopup'
import VipPassOpenBoxAnimation from '@src/components/battle/demo/vip-pass/VipPassOpenBoxAnimation'
import ENV_CONFIG from '@src/config'
import { useVipPassContext } from '@src/contexts/battle/demo/VipPassContext'
import * as R from 'ramda'
import * as RA from 'ramda-adjunct'
import { useCallback, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
`

const Container = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 30px;
  background-color: #1e1e1e;
  border-radius: 10px;
  width: 100%;
  height: 500px;
  margin: 0 auto;
`

const Title = styled(Typography)`
  color: #ffffff;
  font-weight: 700;
  font-size: 32px;
  margin-bottom: 12px;

  ${props => props.theme.breakpoints.up('res1024')} {
    width: 90%;
  }

  ${props => props.theme.breakpoints.down('res1024')} {
    width: 95%;
  }
`

const CarouselContainer = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  position: relative;
  margin-bottom: 20px;
`

const ChestContainer = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  height: 300px;
  width: 750px;

  ${props => props.theme.breakpoints.down('res1024')} {
    width: 50%;
  }

  ${props => props.theme.breakpoints.down('res768')} {
    width: 75%;
  }

  ${props => props.theme.breakpoints.down('res480')} {
    width: 100%;
  }
`

const ChestBox = styled(Box, noForwardProps)<ChestBoxProps>`
  position: absolute;
  transition: all 0.5s ease;
  transform: ${props => (props.__active ? 'scale(1)' : 'scale(0.8)')};
  animation: ${props => props.__active && pulse} 2s infinite ease-in-out;
  top: ${props => (props.__active ? '40px' : '0px')};

  // TODO: fix z-index
  z-index: ${props => (props.__active ? 1 : 0)};

  ${props => props.theme.breakpoints.up('res1024')} {
    width: ${props => (props.__active ? '230px' : '216px')};
    height: ${props => (props.__active ? '260px' : '214px')};
    opacity: ${props =>
      props.__type === 'none' ? 0 : props.__active ? 1 : 0.7};
    left: ${props =>
      props.__type === 'left'
        ? '10px'
        : props.__type === 'center'
        ? '260px'
        : '520px'};
  }

  ${props => props.theme.breakpoints.down('res1024')} {
    width: ${props => (props.__active ? '60%' : '50%')};
    height: ${props => (props.__active ? '60%' : '50%')};
    opacity: ${props => (props.__active ? 1 : 0)};
    left: ${props =>
      props.__type === 'left'
        ? '-25%'
        : props.__type === 'center'
        ? '22%'
        : '75%'};
  }
`

const ChestImage = styled(NextImage)`
  aspect-ratio: 1;

  width: 100%;
`

const NavButton = styled(IconButton)`
  position: absolute;
  color: #00bcd4;

  transition: transform 0.2s ease;
`

const LeftButton = styled(NavButton)`
  left: 0;

  &:hover {
    transform: scale(1.1);
  }
`

const RightButton = styled(NavButton)`
  right: 0;
  transform: scaleX(-1);

  &:hover {
    transform: scaleX(-1) scale(1.1);
  }
`

const ButtonIcon = styled(NextImage)`
  width: 32px;
  height: 46px;
`

const OpenBoxButton = styled(ApeironBlueButton)`
  width: 270px;
  height: 50px;
  border-radius: 2px;

  padding-left: 20px;

  gap: 10px;
`

const KeyIcon = styled(NextImage)`
  position: absolute;
  left: -25px;
  top: -40px;

  aspect-ratio: 0.5;

  height: 120px;
`

const OpenBoxButtonText = styled(Typography)`
  font-size: 20px;
`

const TextLink = styled(NextLink)`
  font-weight: 400;
  margin-left: 10px;
  margin-top: 15px;
  position: relative;
  text-decoration: underline;
  text-shadow: 2px 2px 10px #00000066;
  transition: color ${DEFAULT_ANIMATE_DURATION}ms;

  :hover {
    color: #f1b535;
  }
`

const VipPassLootBoxSection = () => {
  const { t } = useTranslation()

  const { lootBoxData, setCurrentPrize, openLootBox } = useVipPassContext()

  const [activeIndex, setActiveIndex] = useState(0)

  const [animating, setAnimating] = useState(false)

  const [popupOpen, setPopupOpen] = useState(false)

  const [openBoxAnimation, setOpenBoxAnimation] = useState(false)

  const currentLootBox = useMemo(
    () => lootBoxData[activeIndex],
    [activeIndex, lootBoxData]
  )

  const handlePrev = useCallback(() => {
    if (animating) return
    setAnimating(true)
    setActiveIndex(prev => (prev === 0 ? lootBoxData.length - 1 : prev - 1))
    setTimeout(() => setAnimating(false), 200)
  }, [animating, lootBoxData])

  const handleNext = useCallback(() => {
    if (animating) return
    setAnimating(true)
    setActiveIndex(prev => (prev === lootBoxData.length - 1 ? 0 : prev + 1))
    setTimeout(() => setAnimating(false), 200)
  }, [animating, lootBoxData])

  const getChestPositionType = useCallback(
    (index: number): string => {
      const totalChests = lootBoxData.length

      let relativePosition = index - activeIndex

      if (relativePosition < -Math.floor(totalChests / 2)) {
        relativePosition += totalChests
      } else if (relativePosition > Math.floor(totalChests / 2)) {
        relativePosition -= totalChests
      }

      if (relativePosition === 0) return 'center'
      if (
        relativePosition === -1 ||
        (activeIndex === 0 && index === totalChests - 1)
      ) {
        return 'left'
      }

      if (
        relativePosition === 1 ||
        (activeIndex === totalChests - 1 && index === 0)
      ) {
        return 'right'
      }

      return 'none'
    },
    [activeIndex, lootBoxData]
  )

  const handleOpenBox = useCallback(async () => {
    const success = await openLootBox(currentLootBox.assetName)

    if (success) {
      setOpenBoxAnimation(true)

      setTimeout(() => {
        setOpenBoxAnimation(false)
        setPopupOpen(true)
      }, 3500)
    }
  }, [currentLootBox, openLootBox])

  const handleClosePopup = useCallback(() => {
    setPopupOpen(false)

    setTimeout(() => {
      setCurrentPrize(null)
    }, 500)
  }, [setCurrentPrize])

  if (R.isEmpty(lootBoxData)) {
    return <ApeironLoadingNow />
  }

  return (
    <Container>
      <Title>{currentLootBox?.name}</Title>

      <CarouselContainer>
        <ChestContainer>
          {RA.mapIndexed(
            (chest, index) => (
              <ChestBox
                key={index}
                __active={index === activeIndex}
                __type={getChestPositionType(index)}
              >
                <ChestImage src={chest.image} />
              </ChestBox>
            ),
            lootBoxData
          )}
        </ChestContainer>

        <LeftButton onClick={handlePrev}>
          <ButtonIcon src={ButtonIconPNG} />
        </LeftButton>

        <RightButton onClick={handleNext}>
          <ButtonIcon src={ButtonIconPNG} />
        </RightButton>
      </CarouselContainer>

      <OpenBoxButton
        onClick={handleOpenBox}
        disabled={currentLootBox?.keyCount === 0}
      >
        <KeyIcon src={currentLootBox?.keyImage} />
        <OpenBoxButtonText>
          <Trans
            values={{
              lootBoxName: currentLootBox?.name,
              keyCount: currentLootBox?.keyCount
            }}
          >
            {t('vip_pass.loot_box.button_text')}
          </Trans>
        </OpenBoxButtonText>
      </OpenBoxButton>

      <TextLink
        href={ENV_CONFIG.EXTERNAL_URL.BLOG.VIP_KEY_INTRODUCTION}
        target='_blank'
      >
        {`${t('vip_pass.loot_box_section.key_details')} >>`}
      </TextLink>

      <VipPassOpenBoxAnimation
        isOpen={openBoxAnimation}
        boxImage={currentLootBox?.image}
      />

      <VipPassLootBoxPopup
        open={popupOpen}
        onclose={handleClosePopup}
        boxName={currentLootBox?.name}
      />
    </Container>
  )
}

type ChestBoxProps = {
  __active: boolean
  __type: string
}

export default VipPassLootBoxSection
